/**
 * Anthology Jewellers — Appointment Request
 *
 * v2: Uses a MutationObserver on the modal itself instead of a click
 * listener on the trigger button. The page has 14+ other click
 * listeners on document (theme, PageFly, Growave, etc.) and one of
 * them was stopping propagation before our listener ever ran. Watching
 * the modal's own visibility change sidesteps that entirely — the
 * product is added the instant the modal becomes visible, regardless
 * of what caused it to open.
 *
 * Depends on markup in product-form.liquid:
 *   - #modal-appointment-request carrying data-current-title /
 *     data-current-sku / data-current-handle / data-current-url
 *     (set directly from Liquid, not from a button click)
 *   - #AppointmentRequestForm, [data-appointment-item-list],
 *     [data-appointment-error], [data-appointment-success],
 *     [data-appointment-submit], [data-appointment-current-title] etc.
 */

(function () {
  "use strict";

  var WORKER_URL = "https://appointmenttoview.operations-784.workers.dev/";
  var STORAGE_KEY = "anthology_appointment_request";
  var MODAL_ID = "modal-appointment-request";

  // ---- Storage helpers -------------------------------------------------

  function getStoredRequest() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return { customer: {}, items: [] };
      var parsed = JSON.parse(raw);
      if (!parsed.customer) parsed.customer = {};
      if (!Array.isArray(parsed.items)) parsed.items = [];
      return parsed;
    } catch (err) {
      return { customer: {}, items: [] };
    }
  }

  function saveStoredRequest(data) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {}
  }

  function clearStoredRequest() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (err) {}
  }

  function addItem(item) {
    if (!item || !item.sku) return getStoredRequest();
    var data = getStoredRequest();
    var exists = data.items.some(function (existing) {
      return existing.sku === item.sku;
    });
    if (!exists) {
      data.items.push(item);
      saveStoredRequest(data);
    }
    return data;
  }

  function removeItem(sku) {
    var data = getStoredRequest();
    data.items = data.items.filter(function (existing) {
      return existing.sku !== sku;
    });
    saveStoredRequest(data);
    return data;
  }

  function saveCustomerField(field, value) {
    var data = getStoredRequest();
    data.customer[field] = value;
    saveStoredRequest(data);
  }

  // ---- Rendering -------------------------------------------------

  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function renderItemList() {
    var listEl = document.querySelector("[data-appointment-item-list]");
    if (!listEl) return;

    var data = getStoredRequest();

    if (!data.items.length) {
      listEl.innerHTML =
        '<li class="AppointmentRequestForm__EmptyState Text--subdued">No items added yet.</li>';
      return;
    }

    listEl.innerHTML = data.items
      .map(function (item) {
        return (
          '<li data-item-sku="' +
          escapeHtml(item.sku) +
          '">' +
          "<span>" +
          escapeHtml(item.title) +
          "</span>" +
          '<button type="button" data-appointment-remove="' +
          escapeHtml(item.sku) +
          '" aria-label="Remove">&times;</button>' +
          "</li>"
        );
      })
      .join("");
  }

  function populateCustomerFields() {
    var data = getStoredRequest();
    var form = document.getElementById("AppointmentRequestForm");
    if (!form) return;

    ["name", "phone", "email", "notes"].forEach(function (field) {
      var input = form.querySelector('[name="customer_' + field + '"]');
      if (input && data.customer[field]) {
        input.value = data.customer[field];
      }
    });
  }

  function showError(message) {
    var errorEl = document.querySelector("[data-appointment-error]");
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.style.display = message ? "block" : "none";
  }

  function setSubmitting(isSubmitting) {
    var submitBtn = document.querySelector("[data-appointment-submit]");
    var labelEl = document.querySelector("[data-appointment-submit-label]");
    if (!submitBtn) return;
    submitBtn.disabled = isSubmitting;
    if (labelEl) labelEl.textContent = isSubmitting ? "Sending…" : "Send Request";
  }

  function showSuccess() {
    var form = document.getElementById("AppointmentRequestForm");
    var successEl = document.querySelector("[data-appointment-success]");
    if (form) form.style.display = "none";
    if (successEl) successEl.style.display = "block";
  }

  // ---- The core fix: watch the modal, not the click -------------------------------------------------

  function isModalVisible(modalEl) {
    var ariaHidden = modalEl.getAttribute("aria-hidden");
    return ariaHidden === "false" || ariaHidden === null;
  }

  function handleModalOpened(modalEl) {
    var sku = modalEl.getAttribute("data-current-sku");
    if (!sku) return; // nothing to add (e.g. blank SKU on this product)

    addItem({
      title: modalEl.getAttribute("data-current-title") || "",
      sku: sku,
      handle: modalEl.getAttribute("data-current-handle") || "",
      url: modalEl.getAttribute("data-current-url") || "",
    });
    renderItemList();

    // Also reflect the current product up top, since that's what carries
    // the H1-style prominence from the original spec.
    var titleEl = document.querySelector("[data-appointment-current-title]");
    if (titleEl) titleEl.textContent = modalEl.getAttribute("data-current-title") || "";
  }

  function initModalObserver() {
    var modalEl = document.getElementById(MODAL_ID);
    if (!modalEl) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName === "aria-hidden" && isModalVisible(modalEl)) {
          handleModalOpened(modalEl);
        }
      });
    });

    observer.observe(modalEl, { attributes: true, attributeFilter: ["aria-hidden"] });
  }

  // ---- Remove / submit / input — scoped directly to the modal, not document-wide -------------------------------------------------

  function initModalInteractions() {
    var modalEl = document.getElementById(MODAL_ID);
    if (!modalEl) return;

    modalEl.addEventListener("click", function (event) {
      var removeBtn = event.target.closest("[data-appointment-remove]");
      if (removeBtn) {
        removeItem(removeBtn.getAttribute("data-appointment-remove"));
        renderItemList();
      }
    });

    var form = document.getElementById("AppointmentRequestForm");
    if (!form) return;

    form.addEventListener("submit", function (event) {
      event.preventDefault();
      showError("");

      var data = getStoredRequest();

      if (!data.items.length) {
        showError("Please add at least one item before sending.");
        return;
      }

      var customer = {
        name: (form.querySelector('[name="customer_name"]') || {}).value || "",
        phone: (form.querySelector('[name="customer_phone"]') || {}).value || "",
        email: (form.querySelector('[name="customer_email"]') || {}).value || "",
        notes: (form.querySelector('[name="customer_notes"]') || {}).value || "",
      };

      if (!customer.name.trim()) {
        showError("Please enter your name.");
        return;
      }
      if (!customer.phone.trim() && !customer.email.trim()) {
        showError("Please enter a phone number or an email address.");
        return;
      }

      setSubmitting(true);

      fetch(WORKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer: customer, items: data.items }),
      })
        .then(function (res) {
          return res.json().then(function (body) {
            return { ok: res.ok, body: body };
          });
        })
        .then(function (result) {
          setSubmitting(false);
          if (result.ok && result.body && result.body.ok) {
            clearStoredRequest();
            showSuccess();
          } else {
            showError(
              (result.body && result.body.error) ||
                "Something went wrong sending your request. Please try again."
            );
          }
        })
        .catch(function () {
          setSubmitting(false);
          showError("Couldn't reach the server. Please check your connection and try again.");
        });
    });

    form.addEventListener("input", function (event) {
      var input = event.target;
      if (!input.name || input.name.indexOf("customer_") !== 0) return;
      saveCustomerField(input.name.replace("customer_", ""), input.value);
    });
  }

  function init() {
    initModalObserver();
    initModalInteractions();
    renderItemList();
    populateCustomerFields();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();